// Images embedded in the open Design document (M14 photo and logo slots). Bytes live here, keyed by a content
// hash, and are written into the .epd as assets/<id>. Decoded ImageBitmaps are cached for drawing; when one
// finishes decoding, listeners (the tile renderer) redraw the objects that use it.
import type { Doc, Node } from './model/types';

export type Asset = { bytes: Uint8Array; type: string };

const assets = new Map<string, Asset>();
const bitmaps = new Map<string, ImageBitmap | 'loading' | 'error'>();
const listeners = new Set<(id: string) => void>();

const extOf = (type: string) => (type.includes('png') ? 'png' : type.includes('webp') ? 'webp' : 'jpg');
const typeOf = (id: string) =>
  id.endsWith('.png') ? 'image/png' : id.endsWith('.webp') ? 'image/webp' : 'image/jpeg';

async function hash(bytes: Uint8Array): Promise<string> {
  const d = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>));
  return [...d.subarray(0, 12)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Adds image bytes; returns the asset id (`<hash>.<ext>`). The same bytes always give the same id. */
export async function addAsset(bytes: Uint8Array, type: string): Promise<string> {
  const id = `${await hash(bytes)}.${extOf(type)}`;
  if (!assets.has(id)) assets.set(id, { bytes, type });
  return id;
}

export const getAsset = (id: string) => assets.get(id);

/** Replaces the store with a document's assets (opening a file). */
export function loadAssets(entries: Map<string, Uint8Array>) {
  assets.clear();
  for (const b of bitmaps.values()) if (typeof b === 'object') b.close();
  bitmaps.clear();
  for (const [id, bytes] of entries) assets.set(id, { bytes, type: typeOf(id) });
}

/** The assets a document references (what goes into its .epd). */
export function referencedAssets(doc: Doc): Map<string, Uint8Array> {
  const out = new Map<string, Uint8Array>();
  const visit = (n: Node) => {
    if (n.kind === 'image' && n.assetId) {
      const a = assets.get(n.assetId);
      if (a) out.set(n.assetId, a.bytes);
    }
    if (n.kind === 'group') n.children.forEach(visit);
  };
  for (const l of doc.layers) l.children.forEach(visit);
  for (const id of [doc.brand?.logoAsset, doc.brand?.logoLightAsset]) {
    const a = id ? assets.get(id) : undefined;
    if (id && a) out.set(id, a.bytes);
  }
  return out;
}

/** The decoded bitmap if ready; otherwise starts decoding and returns null. */
export function getBitmap(id: string): ImageBitmap | null {
  const b = bitmaps.get(id);
  if (b && typeof b === 'object') return b;
  if (!b) void decode(id);
  return null;
}

async function decode(id: string): Promise<ImageBitmap | null> {
  const a = assets.get(id);
  if (!a) return null;
  bitmaps.set(id, 'loading');
  try {
    const bmp = await createImageBitmap(new Blob([a.bytes as Uint8Array<ArrayBuffer>], { type: a.type }), {
      premultiplyAlpha: 'premultiply',
      colorSpaceConversion: 'default',
    });
    bitmaps.set(id, bmp);
    for (const l of listeners) l(id);
    return bmp;
  } catch {
    bitmaps.set(id, 'error');
    return null;
  }
}

/** Decodes every image a document uses (export waits for this). */
export async function ensureBitmaps(doc: Doc): Promise<void> {
  const ids = [...referencedAssets(doc).keys()];
  await Promise.all(
    ids.map(async (id) => {
      const b = bitmaps.get(id);
      if (b && typeof b === 'object') return;
      if (b === 'loading') {
        await new Promise<void>((resolve) => {
          const l = (got: string) => got === id && (listeners.delete(l), resolve());
          listeners.add(l);
          setTimeout(() => (listeners.delete(l), resolve()), 10_000);
        });
        return;
      }
      await decode(id);
    }),
  );
}

export function onBitmap(fn: (id: string) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
